import React, {useEffect, useMemo, useState} from 'react';
import {handleActiveError, handleActiveToast} from "../../../../../components/sonner/ToastBasic";
import {removeDepreciations} from "../../../../../api/asset/AssetDepreciationService";
import {CacheType, CodeSpecNum} from "../../../../../constant/CodeSpecNum";
import Search from "../../../../../components/search/Search";
import {SearchComboBox} from "../../../../../components/select/SearchComboBox";
import {ASSET_DEPRECIATION_LIST_HEADER} from "../../../../../components/table/header/Headers";
import SearchSection from "../../../../../components/search/SearchSection";
import Selector from "../../../../../components/selector/Selector";
import {searchWithChildren} from "../../../../../components/commonmethod/simmsDrawerMethod";
import {getIndividualSetting, resetIndividualSetting, setIndividualSetting} from "../../../../../api/user/UserAccountService";
import {extractObject, initHeaderOrders, nonObject, simmsColumnsToJsGridHeaders} from "../../../../../components/commonmethod/CommonMethod";
import {useCacheRefresh} from "../../../../../hooks/useCacheRefresh";
import {ToolbarDataTransfer} from "@bavuchoko/js-grid";
import SimmsJsGrid from "../../../../../components/js-grid/SimmsJsGrid";
import {navigateWithChildTab, pickDetailTabTitle} from "../../../../../components/pageTabs/pageTabsStorage";
import {useNavigate} from "react-router-dom";
import {useExcelDownloadClick} from "../../../../../components/commonmethod/excelMethod";
import JsGridExcelGridOpenButton from "../../../../../components/js-grid/JsGridExcelGridOpenButton";

const type = [
    {id: "정률법", name: "정률법"},
    {id: "정액법", name: "정액법"}
];

const year = [
    {id: "1", name: "감가상각 만료 1개월 전"},
    {id: "2", name: "감가상각 만료 3개월 전"},
    {id: "3", name: "감가상각 기간만료"},
];

function AssetDepreciationListType({checked, data, date, dataHook, pageInfo}) {
    const navigate = useNavigate();
    const refreshCache = useCacheRefresh();
    const {handleSearch, handlePageSizeChange, saveConditions, refresh, handleGridPageChange} = dataHook;
    const handleDownloadClick = useExcelDownloadClick(saveConditions);

    const [header, setHeader] = useState({order: ASSET_DEPRECIATION_LIST_HEADER, exclude: []});

    const searchDate = {startDate: saveConditions.startDate ?? date.startDate, endDate: saveConditions.endDate ?? date.endDate};

    useEffect(() => {
        getIndividualSetting().then((r) => {
            initHeaderOrders(r, ASSET_DEPRECIATION_LIST_HEADER, setHeader, [], () => {}, () => {}, 'depreciation');
        });
    }, []);

    const handleRowClick = (row) => {
        const id = row?.id;
        if (id == null) return;
        navigateWithChildTab(navigate, {
            title: pickDetailTabTitle(row, "감가상각 상세"),
            url: `/asset/depreciation/${id}`,
        });
    };

    const deleteDepreciation = async (rows) => {
        if (!rows?.length) {
            handleActiveToast({message: "삭제할 감가상각을 선택해주세요."});
            return;
        }
        if (!window.confirm("삭제하시겠습니까?")) {
            return;
        }
        const ids = rows.map((row) => row.id).filter((id) => id != null);
        await removeDepreciations({ids}).then(response => {
            if (response.status === 200) {
                refreshCache(CacheType.asset);
                handleActiveToast({message: "데이터가 삭제되었습니다"});
            } else if (response.status === 201) {
                handleActiveError({message: response.data.title, description: response.data.reason});
            } else if (response.status === 403) {
                handleActiveError({message: "권한이 없습니다."});
            } else {
                handleActiveError({message: "알 수 없는 에러 발생."});
            }
        });
        checked?.setChecked?.([]);
        refresh();
    };

    const depreciationJsGridHeader = useMemo(() => {
        const converted = simmsColumnsToJsGridHeaders(header);
        if (converted.length > 0) return converted;
        return simmsColumnsToJsGridHeaders({order: ASSET_DEPRECIATION_LIST_HEADER, exclude: []});
    }, [header]);
    const excelGridSearch = useMemo(() => extractObject(saveConditions), [saveConditions]);

    return (
        <div className="container-height">
            <Search refresh={refresh} entity={'depreciation'} searchConditions={saveConditions} handleSearch={handleSearch} handlePageSizeChange={handlePageSizeChange} pageInfo={pageInfo} size detail date={searchDate} searchBar={{placeHolder: '자산명, 계산방법'}}>
                <SearchSection>
                    <Selector showLabel onChange={(value) => searchWithChildren(handleSearch, 'group', extractObject(saveConditions), value)}
                                 data={saveConditions.group}
                                 parentKey={CodeSpecNum.AssetGroupCode} recursive
                                 entity={"code"} title={"자산그룹"} />
                    <Selector showLabel onChange={(value) => searchWithChildren(handleSearch, 'task', extractObject(saveConditions), value)}
                                 data={saveConditions.task}
                                 parentKey={CodeSpecNum.TaskGroupCode} recursive
                                 entity={"code"} title={"업무그룹"} />

                    <SearchComboBox title={"계산방법"} code={type} target={"type"} value={saveConditions}
                                    onChange={(v) => nonObject(saveConditions, v, handleSearch, "type")}
                    />
                    <SearchComboBox title={"감가상각"} code={year} target={"dateType"} value={saveConditions}
                                    onChange={(v) => nonObject(saveConditions, v, handleSearch, "dateType")}
                    />
                </SearchSection>
            </Search>

            <div className="container-height-body">
                <SimmsJsGrid
                    loading={dataHook.loading}
                    resizable={true}
                    data={data}
                    theme={'linear'}
                    style={{minHeight: 0, height: '100%'}}
                    header={depreciationJsGridHeader}
                    onHeaderSave={(v) => setIndividualSetting({headers: {depreciation: v}})}
                    onHeaderReset={() => resetIndividualSetting('headers', 'depreciation')}
                    enableRowSelection={true}
                    onRowClick={handleRowClick}
                    onPageChange={handleGridPageChange}
                    toolbarEnd={() => (
                        <div className="flex items-center gap-2">
                            <JsGridExcelGridOpenButton entityName="ExcelDepreciationDto" search={excelGridSearch} />
                            <ToolbarDataTransfer
                                hint="선택 항목 삭제"
                                busyHint="삭제 중…"
                                overlayLabel="삭제 중…"
                                onTransfer={deleteDepreciation}
                            >
                                {(ctx) => (
                                    <button
                                        type="button"
                                        className="text-[13px] px-3 py-0.5 text-black rounded-4 border disabled:cursor-not-allowed"
                                        disabled={ctx.disabled}
                                        onClick={() => void ctx.transferSelected()}
                                    >
                                        삭제
                                    </button>
                                )}
                            </ToolbarDataTransfer>
                            <button
                                type="button"
                                className="text-[13px] px-3 py-0.5 border text-white bg-themeDark rounded-4"
                                onClick={() => navigate('/asset/depreciation/create')}
                            >
                                등록
                            </button>
                        </div>
                    )}
                />
            </div>
        </div>
    );
}

export default AssetDepreciationListType;
